<template lang="pug">
SideOverlaySlot(
  :isOpen="isOpen",
  title="REFERRAL FOLLOW-UP",
  @closeOverlay="closeOverlay"
)
  template(#contentTop)
    section.patient-section
      .patient-section__top
        p.bold.patient-name {{ showReferral?.patient?.first }} {{ showReferral?.patient?.last }}
        p.small-text
          span Date/Time: {{ getDateFromUnixFormatted(showReferral?.scheduledAt) }} {{ getTimeFromUnixFormatted(showReferral?.scheduledAt) }}
        p.small-text
          span Referral Type: {{ showReferral?.patientNavigationSetting?.name || 'N/A' }}
        p.bold
          span Provider/Location: {{ showReferral?.providerLocation || 'N/A' }}
    form#follow-up-form(
      @submit.prevent="saveFollowUp",
      novalidate
    )
      .columns.is-variable
        .column.is-half
          .form-group
            label(for="action") Action
            select#action(v-model="followUp.action")
              option(value="" disabled) Please Select
              option(
                v-for="option in actionOptions"
                :key="option.value"
                :value="option.value"
              ) {{ option.text }}
        .column.is-half
          .form-group
            label(for="followUpType") Type of Follow Up
            select#followUpType(v-model="followUp.followUpType")
              option(value="" disabled) Please Select
              option(
                v-for="type in followUpTypes"
                :key="type.value"
                :value="type.value"
              ) {{ type.name }}
      .columns.is-variable
        .column.is-half
          .form-group
            label(for="ableToReach") Able to Reach Patient
            select#ableToReach(v-model="followUp.ableToReach")
              option(value="" disabled) Please Select
              option(value="yes") Yes
              option(value="no") No
              option(value="n_a") N/A
        .column.is-half
          .form-group
            label(for="wentToAppointment") Did They Go to Appointment
            select#wentToAppointment(v-model="followUp.wentToAppointment")
              option(value="" disabled) Please Select
              option(value="yes") Yes
              option(value="no") No
              option(value="n_a") N/A
      .columns
        .column
          .form-group
            label(for="notes") Notes
            textarea#notes(v-model="followUp.notes" rows="4" placeholder="Add notes")

  template(#contentBottom)
    .buttons-row.flex.flex--space-between
      BaseBtn.x-lg.red-outline(
        href="#",
        @click.prevent="closeOverlay"
      ) CANCEL
      BaseBtn.x-lg.green(
        type="submit",
        form="follow-up-form",
        :disabled="!isFormValid"
      ) CONTINUE

</template>

<script>
import { defineComponent } from "vue";
import SideOverlaySlot from "@/components/overlays/SideOverlaySlot.vue";

import {
  getDateFromUnixFormatted,
  getTimeFromUnixFormatted,
  getDobFormattedforDateField,
  getPhoneFormatted,
} from "@/helpers";

import { FOLLOWUP_TYPE_OPTIONS } from "@/helpers/constants";

export default defineComponent({
  components: {
    SideOverlaySlot,
  },
  emits: ["closeAddFollowUpOverlay", "followUpSaved"],

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    showReferral: {
      type: Object,
    },
  },

  data() {
    return {
      getDateFromUnixFormatted: getDateFromUnixFormatted,
      getTimeFromUnixFormatted: getTimeFromUnixFormatted,
      getDobFormattedforDateField: getDobFormattedforDateField,
      getPhoneFormatted: getPhoneFormatted,

      followUpTypes: FOLLOWUP_TYPE_OPTIONS,

      followUp: {
        action: "",
        ableToReach: "",
        followUpType: "",
        wentToAppointment: "",
        notes: "",
      },
      showRescheduleForm: false,
    };
  },

  computed: {
    isFirstFollowUpDue() {
      return this.showReferral?.isFirstFollowUpDue || false;
    },

    isSecondFollowUpDue() {
      return this.showReferral?.isSecondFollowUpDue || false;
    },

    actionOptions() {
      const options = [];

      // Add Note Only / 1st follow up option (hidden for second follow-up)
      // if (!this.isSecondFollowUpDue) {
      const value = this.isFirstFollowUpDue
        ? "1st_follow_up"
        : "additional_notes";
      const text = "Add Note Only";
      options.push({ value, text });
      // }

      // Always available options
      options.push(
        { value: "complete_referral", text: "Note & Complete" },
        { value: "reschedule", text: "Note & Reschedule" }
      );

      return options;
    },

    isFormValid() {
      return (
        this.followUp.action !== "" &&
        this.followUp.followUpType !== "" &&
        this.followUp.ableToReach !== "" &&
        this.followUp.wentToAppointment !== "" &&
        this.followUp.notes.trim() !== ""
      );
    },
  },

  methods: {
    closeOverlay() {
      this.resetForm();
      this.$emit("closeAddFollowUpOverlay");
    },

    resetForm() {
      this.followUp = {
        action: "",
        ableToReach: "",
        followUpType: "",
        wentToAppointment: "",
        notes: "",
      };
    },

    async saveFollowUp() {
      // Check if this is a reschedule action
      if (this.followUp.action === "reschedule") {
        this.handleReschedule();
        return;
      }

      // Check if this is a complete & schedule new action
      if (this.followUp.action === "complete_schedule_new") {
        this.handleCompleteScheduleNew();
        return;
      }

      // Validate required fields
      const followUpPayload = {
        action: this.followUp.action,
        followUpType: this.followUp.followUpType,
        ableToReach: this.followUp.ableToReach,
        wentToAppointment: this.followUp.wentToAppointment,
        notes: this.followUp.notes || null,
        idPatientReferral: this.showReferral?.id,
        scheduledAt: this.showReferral?.scheduledAt,
      };

      this.$emit("followUpSaved", followUpPayload);
      this.closeOverlay();
    },

    getRescheduleCount(rescheduleTag) {
      if (!rescheduleTag) return 0;
      if (rescheduleTag === "1ST_RESCHEDULE") return 1;
      if (rescheduleTag === "2ND_RESCHEDULE") return 2;
      return 0;
    },

    handleReschedule() {
      // First save the reschedule follow-up to mark the referral as rescheduled
      const followUpPayload = {
        action: "complete_referral", // "reschedule"
        followUpType: this.followUp.followUpType,
        ableToReach: this.followUp.ableToReach,
        wentToAppointment: this.followUp.wentToAppointment,
        notes: this.followUp.notes || null,
        idPatientReferral: this.showReferral?.id,
        scheduledAt: this.showReferral?.scheduledAt,
      };

      this.$emit("followUpSaved", followUpPayload);

      // Determine reschedule type based on current reschedule count
      const currentRescheduleCount = this.getRescheduleCount(
        this.showReferral.rescheduleTag
      );
      const rescheduleType = currentRescheduleCount === 0 ? "1st" : "2nd";

      // Close the overlay first
      this.closeOverlay();

      // Navigate to reschedule route which redirects to scheduling flow
      this.$router.push({
        name: "cco-patient-navigation.reschedule",
        params: {
          idCommunity: this.showReferral.idCommunity,
          idPatientNavigation: this.showReferral.id,
          rescheduleType: rescheduleType,
        },
      });
    },

    handleCompleteScheduleNew() {
      // First save the follow-up data
      const followUpPayload = {
        action: this.followUp.action,
        followUpType: this.followUp.followUpType,
        ableToReach: this.followUp.ableToReach,
        wentToAppointment: this.followUp.wentToAppointment,
        notes: this.followUp.notes || null,
        idPatientReferral: this.showReferral?.id,
        scheduledAt: this.showReferral?.scheduledAt,
      };

      this.$emit("followUpSaved", followUpPayload);

      // Close the overlay
      this.closeOverlay();

      // Navigate directly to select-patient screen with search already performed
      this.$router.push({
        name: "cco-patient-navigation.scheduling.select-patient",
        params: {
          idCommunity: this.showReferral.idCommunity,
        },
        query: {
          searchBy: "name",
          firstName: this.showReferral?.patient?.first || "",
          lastName: this.showReferral?.patient?.last || "",
          mrn: "",
          dob: "",
        },
      });
    },
  },

  watch: {
    showReferral: {
      handler() {
        this.followUp.action = "";
      },
      immediate: true,
    },
  },
});
</script>

<style scoped lang="scss">
.patient-section {
  position: relative;
  z-index: 1;
  padding: 30px 24px 35px;
  background-color: $primaryBgBlue;
  color: $primaryLightBlue;
  &__top {
    margin-bottom: 8px;
    p {
      margin: 0;
      font-size: 16px;
      line-height: 1.67;
      color: #019bdf;

      &.patient-name {
        font-size: 21px;
        margin-bottom: 8px;
      }

      &.small-text {
        font-size: 16px;
        margin-bottom: 4px;
      }

      &.bold {
        font-weight: 700;
      }
    }
  }
}

.buttons-row {
  margin-top: 24px;
  gap: 16px;
}

form#follow-up-form {
  margin: 47px 0 43px;
  .form-group textarea {
    resize: vertical;
    min-height: 120px;
  }
}
</style>
